import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import PageShell from "./PageShell";
import { Reveal, Overline } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/content";

const EASE = [0.16, 1, 0.3, 1];

export default function MembershipPage() {
  return (
    <PageShell testId="page-membership">
      {/* Hero */}
      <section
        className="relative min-h-[80svh] w-full overflow-hidden"
        data-testid="membership-hero"
      >
        <motion.img
          src={IMAGES.restoreYoga}
          alt="A private yoga sanctuary by firelight"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: EASE }}
          className="absolute inset-0 h-full w-full object-cover img-cinematic"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-bg" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 50% at 50% 50%, rgba(0,0,0,0.75), rgba(0,0,0,0.35) 55%, transparent 82%)",
          }}
        />

        <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12 min-h-[80svh] flex flex-col items-center justify-center text-center pb-16 md:pb-24">
          <Reveal>
            <span
              className="font-sans text-xs md:text-sm tracking-[0.35em] uppercase text-metallic-gold drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]"
              data-testid="membership-eyebrow"
            >
              Membership · Coming Soon
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1
              className="mt-6 font-serif font-light text-ink text-5xl md:text-7xl lg:text-8xl leading-[1] max-w-4xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.9)]"
              data-testid="membership-title"
            >
              Something{" "}
              <em className="italic text-metallic-gold">quiet</em>{" "}
              is on the way.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              className="mt-8 font-serif italic text-lg md:text-xl lg:text-2xl text-metallic-gold max-w-2xl leading-snug"
              data-testid="membership-tagline"
            >
              Stand by — watch this space. Food and Yoga classes on the way.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <section
        className="relative bg-bg py-24 md:py-36"
        data-testid="membership-body"
      >
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 text-center">
          <Reveal>
            <Overline>An Invitation, Soon</Overline>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="mt-8 font-serif font-light text-ink text-3xl md:text-4xl lg:text-5xl leading-[1.1] max-w-3xl mx-auto">
              A private circle for stillness, movement, breath and the slow art
              of restoration.
            </h2>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-10 font-sans text-base md:text-lg text-ink/65 max-w-2xl mx-auto leading-relaxed">
              We&rsquo;re quietly preparing something considered — a private
              video library sharing <em className="text-metallic-gold not-italic">healing food</em> and
              {" "}
              <em className="text-metallic-gold not-italic">yoga practice</em>
              {" "}
              for members. Weekly recipe films, restorative yoga sessions,
              breathwork and mobility videos — created to infuse naturally into
              everyday life.
              <span className="block mt-4 text-metallic-gold">
                Food and yoga videos coming soon.
              </span>
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <figure className="mt-12 max-w-3xl mx-auto">
              <blockquote
                className="font-serif italic text-2xl md:text-3xl lg:text-4xl text-ink/85 leading-[1.25]"
                data-testid="membership-quote"
              >
                <span className="text-metallic-gold">&ldquo;</span>You are best
                to practice your own form of yoga, however imperfectly that may
                be, than to practice the yoga of another perfectly.
                <span className="text-metallic-gold">&rdquo;</span>
              </blockquote>
              <figcaption className="mt-6 font-sans text-xs tracking-[0.35em] uppercase text-gold">
                — Bhagavad Gita
              </figcaption>
            </figure>
          </Reveal>

          {/* Three pillars of what's coming */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-4xl mx-auto text-left">
            <Reveal delay={0.1}>
              <div
                className="bg-bg p-8 h-full"
                data-testid="membership-teaser-yoga"
              >
                <span className="font-sans text-[0.65rem] tracking-[0.3em] uppercase text-oxblood-soft">
                  01 &nbsp;·&nbsp; Yoga
                </span>
                <p className="mt-5 font-sans text-sm text-ash leading-relaxed">
                  Private, small-group and virtual classes designed for real
                  bodies and real lives.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div
                className="bg-bg p-8 h-full"
                data-testid="membership-teaser-breath"
              >
                <span className="font-sans text-[0.65rem] tracking-[0.3em] uppercase text-gold">
                  02 &nbsp;·&nbsp; Breath &amp; Stillness
                </span>
                <p className="mt-5 font-sans text-sm text-ash leading-relaxed">
                  Breathwork, meditation and quiet practice — a moment of
                  regulation in every day.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.26}>
              <div
                className="bg-bg p-8 h-full"
                data-testid="membership-teaser-restore"
              >
                <span className="font-sans text-[0.65rem] tracking-[0.3em] uppercase text-forest-soft">
                  03 &nbsp;·&nbsp; Restore
                </span>
                <p className="mt-5 font-sans text-sm text-ash leading-relaxed">
                  Recovery rituals, mobility work and nourishment — care that
                  compounds over time.
                </p>
              </div>
            </Reveal>
          </div>

          {/* Waitlist CTA */}
          <Reveal delay={0.3}>
            <div className="mt-16 flex flex-col items-center gap-4">
              <p className="font-serif italic text-xl md:text-2xl text-metallic-gold">
                Would you like to be first to know?
              </p>
              <Link
                to="/book?interest=membership"
                data-testid="membership-waitlist-btn"
                className="inline-flex items-center gap-4 rounded-full bg-black border border-gold/70 px-8 py-4 font-sans text-xs tracking-[0.2em] uppercase text-metallic-gold hover:border-gold hover:bg-black/80 transition-all duration-300"
              >
                Join the Waitlist
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </Link>
              <p className="mt-2 font-sans text-xs tracking-[0.25em] uppercase text-faint">
                Or write to hello@lodgeandretreatpartners.nz
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
